/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { v4 as uuidv4 } from 'uuid';
import { FormApi } from 'final-form';

import { IKnmAttachment } from 'APIPackage/KnmModule/interfaces/IKnmAttachment';
import { useKnmDocumentAttachment } from 'KnmPackage/MainModule/hooks/useKnmDocumentAttachment';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { KnmSignDocumentModal } from 'KnmPackage/KnmDocumentsModule/components/KnmSignDocumentModal';
import { KnmAttachmentsList } from 'KnmPackage/MainModule/components/KnmAttachmentsList';
import { IKnmFormAttachment } from 'KnmPackage/MainModule/interfaces/IKnmFormAttachment';
import { IKnmAttachmentDocument } from 'KnmPackage/KnmDocumentsModule/interfaces/IKnmAttachmentDocument';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { DONT_HAVE_INTERNET_CONNECTION } from 'CorePackage/MainModule/constants/notices';

interface IKnmAttachmentsSigningProps {
  acceptedFiles?: string;
  closeSignModal: () => void;
  formRef: IKnmFormRef | IDecisionFormRef | React.MutableRefObject<FormApi<any, Partial<any>> | undefined> | undefined;
  isRequired?: boolean;
  isWatchOnly?: boolean;
  isVisibleSignModal: boolean;
  name: string;
  isShowDateTime?: boolean;
}

export const KnmAttachmentsSigning = memo(
  ({
    acceptedFiles,
    closeSignModal,
    formRef,
    isRequired,
    isWatchOnly,
    isVisibleSignModal,
    name,
    isShowDateTime,
  }: IKnmAttachmentsSigningProps) => {
    const push = formRef?.current?.mutators.push;
    const remove = formRef?.current?.mutators.remove;
    const dispatch = useDispatch<Dispatch<RootActions>>();
    const { uploadKnmDocumentAttachment } = useKnmDocumentAttachment();

    const addAttachment = useCallback(
      (data: IKnmAttachmentDocument) => {
        !isWatchOnly && push && push(name, data);
      },
      [name, push, isWatchOnly],
    );

    const handleSaveSignedDocument = useCallback(
      async (signedDocument: File, signatureInfo: IKnmAttachment['signatureInfo']) => {
        const listId = uuidv4();

        addAttachment({
          fileName: signedDocument.name,
          fileSize: Math.trunc(signedDocument.size / 1000) || 0,
          isNew: true,
          listId,
          file: signedDocument,
        });

        let uploadedAttachment: IKnmAttachment | undefined;

        try {
          uploadedAttachment = await uploadKnmDocumentAttachment(signedDocument);

          if (uploadedAttachment) {
            // @ts-ignore
            const attachments = formRef?.current.getFieldState(name as keyof IKnmForm)?.value as
              | IKnmFormAttachment[]
              | undefined;
            const uploadedAttachmentIndex = attachments?.findIndex(item => item.listId === listId);

            if (typeof uploadedAttachmentIndex === 'number' && uploadedAttachmentIndex >= 0) {
              const attachmentFieldName = `${name}[${uploadedAttachmentIndex}]` as keyof IKnmForm;
              const newUploadedAttachment: IKnmFormAttachment = {
                ...uploadedAttachment,
                signatureInfo,
                signed: true,
                isNew: true,
              };

              // @ts-ignore
              formRef?.current.change(attachmentFieldName, newUploadedAttachment);
            }
          }
        } catch (error) {
          /**
           * загрузка файла не удалась (например, запрос заблокирован/нет соединения):
           * чтобы сага не показала сообщение об успешном добавлении
           */
          // @ts-ignore
          const currentAttachments = formRef?.current.getFieldState(name as keyof IKnmForm)?.value as
            | IKnmFormAttachment[]
            | undefined;
          const failedAttachmentIndex = currentAttachments?.findIndex(item => item.listId === listId);

          if (typeof failedAttachmentIndex === 'number' && failedAttachmentIndex >= 0) {
            remove && remove(name, failedAttachmentIndex);
          }

          dispatch(
            addNoticeAction({
              renderContent: DONT_HAVE_INTERNET_CONNECTION,
              noticeType: NoticeTypesEnum.ERROR,
              messageType: MessageTypesEnum.VALIDATE_DOCUMENT,
            }),
          );

          throw error;
        } finally {
          closeSignModal();
        }
      },
      [addAttachment, closeSignModal, dispatch, formRef, name, remove, uploadKnmDocumentAttachment],
    );

    return (
      <>
        <KnmAttachmentsList
          name={name}
          isRequired={isRequired}
          isWatchOnly={isWatchOnly}
          removeField={!isWatchOnly ? remove : undefined}
          isShowDateTime={isShowDateTime}
        />
        <KnmSignDocumentModal
          isVisibleModal={isVisibleSignModal}
          fieldName={name}
          onCloseSignModal={closeSignModal}
          onSaveSignedDocument={handleSaveSignedDocument}
          isWatchOnly={isWatchOnly}
          acceptedFiles={acceptedFiles}
        />
      </>
    );
  },
);
